import axios, { AxiosError } from 'axios';

// Define ApiError interface here since it's not being imported correctly
export interface ApiError {
  message?: string;
  code?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

/**
 * Extracts error message from different error types
 * @param error Error object from catch block
 * @returns Human-readable error message
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle Axios errors
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    
    // Network error (no response)
    if (!axiosError.response) {
      return 'Network error. Please check your internet connection.';
    }
    
    // Server responded with an error
    if (axiosError.response.data) {
      // Use the server-provided error message if available
      if (typeof axiosError.response.data === 'string') {
        return axiosError.response.data;
      }
      
      if (axiosError.response.data.message) {
        return axiosError.response.data.message;
      }
      
      // Handle validation errors
      if (axiosError.response.data.errors) {
        const errors = axiosError.response.data.errors;
        const firstErrorField = Object.keys(errors)[0];
        if (firstErrorField && errors[firstErrorField].length > 0) {
          return errors[firstErrorField][0];
        }
      }
    }
    
    // Fallback to HTTP status text
    return `Error: ${axiosError.response.status} ${axiosError.response.statusText}`;
  }
  
  // Handle regular Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Fallback for unknown error types
  return 'An unknown error occurred';
};

/**
 * Checks if an error is a network connectivity error
 * @param error Error object from catch block
 * @returns True if it's a network connectivity error
 */
export const isNetworkError = (error: unknown): boolean => {
  if (axios.isAxiosError(error)) {
    return !error.response && !!error.request;
  }
  return false;
};

/**
 * Checks if an error is an authentication error (401)
 * @param error Error object from catch block
 * @returns True if it's an authentication error
 */
export const isAuthError = (error: unknown): boolean => {
  if (axios.isAxiosError(error) && error.response) {
    return error.response.status === 401;
  }
  return false;
};

/**
 * Checks if an error is a validation error (400 with errors object)
 * @param error Error object from catch block
 * @returns True if it's a validation error
 */
export const isValidationError = (error: unknown): boolean => {
  if (axios.isAxiosError(error) && error.response) {
    return (
      error.response.status === 400 &&
      error.response.data &&
      !!error.response.data.errors
    );
  }
  return false;
};

